// CloudBackup.tsx — the canonical data-portability control. IDENTICAL in every data-holding
// Oriora app (props only) — the drop-in half of the fleet backup standard, alongside the
// generic backup.rs engine. Renders:
//   • Back up / Restore to the user's OWN cloud (GitHub or Google Drive, via the server relay
//     in customer-store) — plain HTTP, so it works on iOS with no file picker.
//   • Desktop-only Export file / Import file (native Save/Open dialog) for an offline copy.
// The user owns their data: a faithful, re-importable JSON envelope they can move anywhere.
import { useCallback, useEffect, useRef, useState } from "react";
import { save as saveFileDialog, open as openFileDialog, ask } from "@tauri-apps/plugin-dialog";
import { writeTextFile, readTextFile } from "@tauri-apps/plugin-fs";
import { invoke } from "./tauri-web";
import { platform } from "./tauri-web";
import { exportBackup, importBackup, backupFilename } from "./backup";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, BrainError } from "./brain";
import type { CloudBackupStatusInfo } from "./brain";
import { connectGithubRestoreIos, connectGithubRestoreMac, GithubRestoreError } from "./ios-native-kit";

const cloudName = (p?: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
// Day-only precision made a same-day re-backup look like nothing happened — "last backup 16 Sep"
// is identical text before and after a fresh backup taken an hour later. Adding the time makes a
// fresh backup visibly fresh.
const slotDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : null;
// Desktop shows the extra local-file buttons; mobile keeps just cloud Back up/Restore.
function onDesktop(): boolean {
  try { return platform() !== "ios" && platform() !== "android"; } catch { return true; }
}
function isWeb(): boolean {
  try { return platform() === "web"; } catch { return false; }
}
// Import (restore) also works on iOS: UIDocumentPickerViewController surfaces Google Drive
// (and iCloud Drive/Files) the same way NSOpenPanel does on desktop — the customer picks a
// file, the OS hands the app scoped access, no cloud credential ever involved. This is the
// one restore path that bypasses the server-relay's ~1MB GitHub Contents-API ceiling entirely.
// Web is excluded HERE and gets its own path below: @tauri-apps/plugin-dialog is a native
// Tauri plugin with no browser bridge underneath it — calling it on a plain web page throws,
// it doesn't gracefully no-op. Android excluded too — not a built/supported target for this fleet.
function canImportFile(): boolean {
  try { return platform() !== "android" && platform() !== "web"; } catch { return true; }
}
// Device-side GitHub restore: native-only (Mac + iOS), reusing the same auth-session runners as
// sign-in. Not offered on web — no Keychain there, see the doc's "not yet decided" note. Not
// offered on Android — not a built/supported target for this fleet.
function canGithubRestore(): boolean {
  try { const p = platform(); return p === "macos" || p === "ios"; } catch { return false; }
}

// window.confirm() is a silent no-op in Tauri's iOS WKWebView (no WKUIDelegate panel is wired up
// for it) — it returns without ever showing a dialog, so every destructive gate that used it was
// proceeding unconfirmed on-device (found + proven on the iOS Simulator, 2026-09-17, rolled
// fleet-wide same day). On native, use the real OS dialog (@tauri-apps/plugin-dialog, already a
// dependency for the file picker below); on web the plugin doesn't exist, so window.confirm
// (which works fine in a browser) stays the path there. Exported so any destructive confirm in
// this app — not just this file's own four — uses the one real implementation rather than a
// second hand-rolled copy.
export function confirmDestructive(appName: string, message: string): Promise<boolean> {
  return isWeb() ? Promise.resolve(window.confirm(message)) : ask(message, { title: appName, okLabel: "Continue", cancelLabel: "Cancel" });
}

export function CloudBackup({ appName, fileSlug, appScheme, hasData, onRestored }: {
  /** Human app name, used in confirm/result copy (e.g. "Recipe Box"). */
  appName: string;
  /** Filename stem for the local-file export (e.g. "recipe-box"). */
  fileSlug: string;
  /** This app's URL scheme (e.g. "oriora-plant-diary") — needed for the GitHub-restore OAuth
   *  callback. Scheme naming isn't perfectly mechanical across the fleet (e.g.
   *  "ai-health-tracker-coach" has no "oriora-" prefix), so it's an explicit prop, not derived. */
  appScheme: string;
  /** Whether there's anything to back up (hides "Back up" on an empty collection). */
  hasData: boolean;
  /** Called after a successful restore so the host can reload its state. */
  onRestored: () => void | Promise<void>;
}) {
  const [msg, setMsg] = useState<string | null>(null);
  const isDesktop = onDesktop();
  const canImport = canImportFile();
  const webImport = isWeb();
  const ghRestoreAvailable = canGithubRestore();
  const [ghConnected, setGhConnected] = useState(false);
  // The RESTORE STORY status line — fetched on panel open so the source is shown BEFORE any press.
  const [status, setStatus] = useState<CloudBackupStatusInfo | null | "loading">("loading");
  const webFileInput = useRef<HTMLInputElement>(null);
  const err = (e: unknown, verb: string) =>
    setMsg(e instanceof BrainError ? e.message : `${verb} failed — ${e instanceof Error ? e.message : String(e)}`);
  const confirmRestore = (message: string): Promise<boolean> => confirmDestructive(appName, message);

  useEffect(() => {
    let live = true;
    cloudBackupStatus().then((s) => { if (live) setStatus(s); }).catch(() => { if (live) setStatus(null); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!ghRestoreAvailable) return;
    void invoke<boolean>("has_github_restore_token").then(setGhConnected).catch(() => {});
  }, [ghRestoreAvailable]);

  const disconnectGithub = useCallback(() => {
    void (async () => {
      try {
        await invoke("clear_github_restore_token");
        setGhConnected(false);
        setMsg("GitHub disconnected on this device.");
      } catch (e) { err(e, "Disconnect"); }
    })();
  }, []);

  const connectGithub = useCallback(() => {
    void (async () => {
      try {
        if (platform() === "ios") await connectGithubRestoreIos(appScheme);
        else await connectGithubRestoreMac(appScheme);
        setGhConnected(true);
        setMsg("GitHub connected — this device now backs up and restores directly with your GitHub, any size, photos included.");
      } catch (e) {
        setMsg(e instanceof GithubRestoreError ? e.message : `Connecting GitHub failed — ${e instanceof Error ? e.message : String(e)}`);
      }
    })();
  }, [appScheme]);

  const githubRestore = useCallback(() => {
    void (async () => {
      try {
        if (!(await confirmRestore(`Restore will REPLACE everything in ${appName} with your GitHub backup. Continue?`))) return;
        await invoke("restore_from_github_token");
        await onRestored();
        setMsg("Restored from GitHub — your data is back.");
      } catch (e) { err(e, "GitHub restore"); }
    })();
  }, [appName, onRestored]);

  // Device-direct GitHub save — the write half of the device connection. No size ceiling: the
  // file goes straight from this device to the customer's GitHub, never through Oriora's server.
  const githubBackup = useCallback(async () => {
    await invoke("backup_to_github_token");
    setGhConnected(true);
    setMsg("Backed up straight to your GitHub from this device — any size, photos included.");
  }, []);

  const cloudBackup = useCallback(() => {
    void (async () => {
      try {
        const { provider } = await cloudBackupSave(JSON.parse(await exportBackup()));
        setMsg(`Backed up to your ${cloudName(provider)} — restore it on any device.`);
      } catch (e) {
        // Too large for the standard (server-relay) path → fall through to the device-direct
        // GitHub save when it's available, instead of surfacing a dead-end error.
        if (e instanceof BrainError && e.code === "too_large") {
          if (ghRestoreAvailable && ghConnected) {
            try { await githubBackup(); return; } catch (e2) { err(e2, "GitHub backup"); return; }
          }
          setMsg(ghRestoreAvailable
            ? "This backup is too large for the standard path (it has photos). Connect GitHub below — large backups then save directly from this device, any size."
            : "This backup is too large for the standard path. Use Export file here, or back up from the iPhone/Mac app where large backups save directly to GitHub.");
          return;
        }
        err(e, "Cloud backup");
      }
    })();
  }, [ghRestoreAvailable, ghConnected, githubBackup]);

  // Explicit-source restore: the button NAMES the provider it reads (never a mystery "Restore").
  const cloudRestore = useCallback((provider?: "github" | "gdrive") => {
    void (async () => {
      try {
        const st = await cloudBackupLoad(provider);
        if (!st.connected) { setMsg("Connect GitHub or Google Drive in your Oriora account to use cloud backup."); return; }
        if (!st.data) { setMsg("No cloud backup found yet — back up first."); return; }
        const src = cloudName(st.provider);
        if (!(await confirmRestore(`Restore will REPLACE everything in ${appName} with your ${src} backup. Continue?`))) return;
        await importBackup(JSON.stringify(st.data));
        await onRestored();
        setMsg(`Restored from your ${src} — your data is back.`);
      } catch (e) { err(e, "Cloud restore"); }
    })();
  }, [appName, onRestored]);

  const exportFile = useCallback(() => {
    void (async () => {
      try {
        const json = await exportBackup();
        if (webImport) {
          // Browser: no native save dialog — a plain blob download to the computer.
          const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
          const a = document.createElement("a");
          a.href = url; a.download = backupFilename(fileSlug); a.click();
          URL.revokeObjectURL(url);
          setMsg("Backup file downloaded — keep it, or drop it in iCloud/Drive.");
          return;
        }
        const p = await saveFileDialog({ defaultPath: backupFilename(fileSlug) });
        if (p) { await writeTextFile(p, json); setMsg("Backup file saved — keep it, or drop it in iCloud/Drive."); }
      } catch (e) { err(e, "Export"); }
    })();
  }, [fileSlug, webImport]);

  const importFile = useCallback(() => {
    void (async () => {
      try {
        const picked = await openFileDialog({ multiple: false, filters: [{ name: "Oriora backup", extensions: ["json"] }] });
        const path = typeof picked === "string" ? picked : Array.isArray(picked) ? picked[0] ?? null : null;
        if (!path) return;
        const json = await readTextFile(path);
        if (!(await confirmRestore(`Restore will REPLACE everything in ${appName} with this file. Continue?`))) return;
        await importBackup(json);
        await onRestored();
        setMsg("Restored from file — your data is back.");
      } catch (e) { err(e, "Import"); }
    })();
  }, [appName, onRestored]);

  // Web equivalent of importFile above: there's no Tauri IPC bridge in a plain browser tab,
  // so @tauri-apps/plugin-dialog can't be used — a real <input type="file"> + FileReader is
  // the browser's own native picker instead (also shows Drive/iCloud if the OS surfaces them
  // to the browser's file dialog, same as it does for any other web app's file input).
  const importFileWeb = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same filename later
    if (!file) return;
    void (async () => {
      try {
        const json = await file.text();
        if (!(await confirmRestore(`Restore will REPLACE everything in ${appName} with this file. Continue?`))) return;
        await importBackup(json);
        await onRestored();
        setMsg("Restored from file — your data is back.");
      } catch (err_) { err(err_, "Import"); }
    })();
  }, [appName, onRestored]);

  const sectionLabel = (text: string) => (
    <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, marginTop: "0.7rem", marginBottom: "0.15rem" }}>{text}</div>
  );
  const sectionHint = (text: string) => (
    <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: "0.4rem" }}>{text}</div>
  );

  return (
    // One block-level wrapper: the host page mounts <CloudBackup/> inside its own flex row
    // (alongside an Export-CSV button etc.) — without this, each top-level element below becomes
    // its own flex item and the section labels/hints wrap onto the same line as the buttons.
    <div style={{ width: "100%" }}>
      {/* Account-level cloud backup — the RESTORE STORY (CUSTOMER-CLOUD-STORAGE.md): the status
          line shows WHERE backups live BEFORE anything is pressed; every restore button NAMES its
          source; when both clouds hold a backup, both appear, dated, and the customer picks. */}
      {sectionLabel("Cloud backup")}
      {status === "loading" ? (
        sectionHint("Checking where your backups live…")
      ) : status === null ? (
        sectionHint("Saves to the cloud connected in your Oriora account (Settings → Connections on orioralabs.com).")
      ) : status.connected.length === 0 ? (
        sectionHint("Nothing connected yet — connect GitHub or Google Drive once at orioralabs.com → Settings → Connections, then every Oriora app can back up there.")
      ) : (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: "0.4rem" }}>
          <span style={{ color: "var(--accent)", fontWeight: 600 }}>
            ● Backing up to: {cloudName(status.writes_to ?? undefined)}
          </span>{" "}
          — connected in your Oriora account
          {status.writes_to && status.slots[status.writes_to]?.backup_exists
            ? ` · last backup ${slotDate(status.slots[status.writes_to]!.updated_at)}`
            : " · no backup saved yet"}
          {status.connected.filter((p) => p !== status.writes_to).map((p) => (
            <div key={p}>
              {cloudName(p)}: also connected, not used for new backups ({cloudName(status.writes_to ?? undefined)} takes priority)
              {status.slots[p]?.backup_exists ? ` · holds a backup from ${slotDate(status.slots[p]!.updated_at)}` : ""}
            </div>
          ))}
        </div>
      )}
      <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
        {hasData && <button className="btn btn-sm btn-pill" onClick={cloudBackup}>Back up</button>}
        {status !== "loading" && status !== null && status.connected.length > 0 ? (
          (["github", "gdrive"] as const)
            .filter((p) => status.slots[p]?.backup_exists)
            .map((p) => (
              <button key={p} className="btn btn-sm btn-pill" onClick={() => cloudRestore(p)}>
                Restore from {cloudName(p)} ({slotDate(status.slots[p]!.updated_at) ?? "saved"})
              </button>
            ))
        ) : (
          <button className="btn btn-sm btn-pill" onClick={() => cloudRestore()}>Restore</button>
        )}
        {(isDesktop || webImport) && hasData && <button className="btn btn-sm btn-pill" onClick={exportFile}>Export file</button>}
      </div>

      {(canImport || webImport) && (
        <>
          {sectionLabel("Restore from a file")}
          {sectionHint("Pick a backup file from Google Drive, iCloud, Files, or this device. No separate connection needed here — if Drive is set up on your phone (in Settings, not in this app), it just appears as an option in the picker.")}
          <div className="row" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
            {canImport && <button className="btn btn-sm btn-pill" onClick={importFile}>Import file</button>}
            {webImport && (
              <>
                <input
                  ref={webFileInput}
                  type="file"
                  accept="application/json,.json"
                  style={{ display: "none" }}
                  onChange={importFileWeb}
                />
                <button className="btn btn-sm btn-pill" onClick={() => webFileInput.current?.click()}>Import file</button>
              </>
            )}
          </div>
        </>
      )}

      {ghRestoreAvailable && (
        <>
          {sectionLabel("Large backups — GitHub on this device")}
          {sectionHint("Separate from signing in to Oriora: log in with GitHub HERE and this device backs up and restores directly with your GitHub — any size, photos included, nothing passing through Oriora. The cloud backup above uses the connection in your Oriora account and is for everyday-sized backups.")}
          <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            {ghConnected && (
              <span className="muted" style={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--accent)" }}>
                ● Connected
              </span>
            )}
            {ghConnected ? (
              <>
                {hasData && (
                  <button className="btn btn-sm btn-pill" onClick={() => { void githubBackup().catch((e) => err(e, "GitHub backup")); }}>
                    Back up to GitHub
                  </button>
                )}
                <button className="btn btn-sm btn-pill" onClick={githubRestore}>Restore from GitHub</button>
              </>
            ) : (
              <button className="btn btn-sm btn-pill" onClick={connectGithub}>Log in with GitHub</button>
            )}
            {ghConnected && <button className="btn-ghost" style={{ fontSize: "var(--fs-xs)" }} onClick={disconnectGithub}>Disconnect</button>}
          </div>
        </>
      )}

      {msg && <div role="status" className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.45rem", width: "100%" }}>{msg}</div>}
    </div>
  );
}
